import { useEffect } from 'react';
import { API, setAuthToken } from '../api/api';
import { transformTimelogsToEvents, handlePeopleTimelogToEvent, transformLeaveToEvent } from '../utils/events';

export function useAuthBootstrap({
  authenticated,
  setAuthenticated,
  setPortalName,
  setProjects,
  setEvents,
  showError,
  setError,
  setBillableAutoFill,
  setCurrentView,
  setFeatureFlags,
}) {
  // Bootstrap auth + initial data
  useEffect(() => {
    // Prefer subdomain, fall back to route (for localhost/dev)
    const hostSub = window.location.hostname.split('.')[0]?.toUpperCase();
    const pathMatch = window.location.pathname.match(/\/portal\/([A-Z0-9_]+)/i);
    const portal = pathMatch?.[1]?.toUpperCase() || hostSub || null;

    if (!portal) {
      setError('No portal specified. Access via /portal/PORTAL_NAME');
      return;
    }

    API.checkAuth()
      .then(async (data) => {
        setPortalName(data.portalName || portal);
        setBillableAutoFill(!!data.billableAutoFill);

        // Store feature flags from server config
        if (setFeatureFlags) {
          setFeatureFlags({
            enablePeople: !!data.enablePeople,
            enableLeave: !!data.enableLeave,
            enableOutlookSync: !!data.enableOutlookSync,
            enableCRM: !!data.enableCRM,
            enableSeries: !!data.enableSeries,
            moduleType: data.moduleType || 'general',
            demoMode: !!data.demoMode,
            directProjectMode: !!data.directProjectMode,
            enableStatsBar: !!data.enableStatsBar,
            enableRolle: !!data.enableRolle,
            filterProjectsByAssignment: !!data.filterProjectsByAssignment,
            isAdmin: !!data.isAdmin,
            employees: data.employees || [],
            currentUserName: data.currentUserName || '',
          });
        }

        if (!data.authenticated) {
          const isMobile = /Mobi|Android/i.test(navigator.userAgent);
          if (isMobile) {
            const { url } = await API.loginPortal(portal);
            window.location.href = url;
            return;
          }
          const { url } = await API.loginPortal(portal);
          window.open(url, 'oauthPopup', 'width=600,height=700');
          const apiBase = process.env.REACT_APP_API_BASE_URL || '';
          const apiOrigin = apiBase ? new URL(apiBase, window.location.href).origin : window.location.origin;
          window.addEventListener('message', function handleOAuthMsg(event) {
            if (event.origin !== window.location.origin && event.origin !== apiOrigin) return;
            if (event.data && event.data.type === 'oauth-error') {
              showError('Access Denied', event.data.message || 'Access denied');
              window.removeEventListener('message', handleOAuthMsg);
              return;
            }
            if (event.data && event.data.type === 'oauth-success') {
              const token = event.data.authToken;
              if (token) {
                setAuthToken(token);
                window.location.hash = 'authToken=' + token;
              }
              window.location.reload();
              window.removeEventListener('message', handleOAuthMsg);
            }
          });
          return;
        }

        setAuthenticated(true);

        // Clear the auth token from the URL hash — it has served its purpose
        if (window.location.hash.includes('authToken=')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        // Fetch user's last calendar view preference
        API.getUserViewPreference().then((viewData) => {
          if (viewData.view && setCurrentView) {
            setCurrentView(viewData.view);
          }
        }).catch(() => {});

        // Fetch projects
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});

        // Fetch timelogs (projects + people if enabled) + leave requests
        const projectsReq = API.refreshTimelogs();
        const peopleReq = data.enablePeople
          ? API.fetchPeopleTimelogs().catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });
        const leaveReq = data.enableLeave
          ? API.fetchLeaveRequests().catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });

        Promise.all([projectsReq, peopleReq, leaveReq])
          .then(([projectsData, peopleData, leaveData]) => {
            const allLogs = projectsData.data?.flatMap(group => group.timelogs || []) || [];
            const projectEvents = transformTimelogsToEvents({ timelogs: { date: allLogs } });

            const peopleLogs = peopleData.data?.flatMap(group => group.timelogs || []) || [];
            const peopleEvents = peopleLogs
              .map(log => handlePeopleTimelogToEvent(log))
              .filter(Boolean);

            const leaveItems = Array.isArray(leaveData.data) ? leaveData.data : [];
            const leaveEvents = leaveItems.flatMap(transformLeaveToEvent);

            setEvents([...projectEvents, ...peopleEvents, ...leaveEvents]);
          })
          .catch(() => {
            showError('Data Error', 'Failed to fetch timelogs. Please try refreshing the page.');
          });
      })
      .catch(() => {
        setError('Connection error - please try again');
      });
  }, [showError]);

  // 30s ping keepalive
  useEffect(() => {
      if (!authenticated) return;
      const interval = setInterval(() => {
        API.ping().catch(() => {
          window.location.reload();
        });
      }, 30000);
      return () => clearInterval(interval);
    }, [authenticated]);
}
